import type { OfferBuilderProperty } from '~/components/Offers/Detail/OfferBuilder/Content/PropertyInformation';

import type { DetailField, DetailSectionId } from './types';

export type DetailFieldLayout = NonNullable<DetailField['layout']>;

export interface BuildDetailsFieldsParams {
  dealCount: number;
  offer?: App.Offer;
  opportunity?: App.OpportunityPage;
  opportunityId?: string;
  property?: OfferBuilderProperty;
}

export type DetailFieldBySection = Record<DetailSectionId, Array<DetailField>>;

export const leftColumn = { column: 1, columns: 2 } as const;
export const rightColumn = { column: 2, columns: 2 } as const;

const REQUIRED_DETAIL_FIELD_LABELS = new Set([
  'Account Name',
  'Admin Fee (%)',
  'Book By',
  'Booking Data Cut-off (Days)',
  'Campaign Type',
  'Close Date',
  'Cost Price Currency',
  'Deal Category',
  'Deal Sub-Category',
  'Deal Tier',
  'Destination',
  'Escalation Email',
  'Escalation Name',
  'Escalation Phone',
  'Includes Solo Traveler Packages',
  'Main Vertical Site',
  'Opportunity Name',
  'Opportunity Owner',
  'Opportunity Record Type',
  'Payment Method',
  'Probability (%)',
  'Product Segment',
  'Sales Person Gross Sales Forecast (AUD)',
  'Sales Person Margin % Forecast',
  'Sales Person Margin Forecast (AUD)',
  'Stage',
  'Sub - Destination',
  'Target Selling Market',
  'Travel From',
  'Travel To',
  'Brands',
]);

function formatValue(value: unknown, readOnly: boolean): string {
  if (value === null || value === undefined || value === '') {
    return readOnly ? 'Not available' : '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : 'Not available';
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return readOnly ? 'Not available' : '';
    }
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }

  return readOnly ? 'Not available' : '';
}

export function fullName(firstName?: string, lastName?: string) {
  return [firstName, lastName].filter(Boolean).join(' ');
}

export function field(
  label: string,
  value: unknown,
  helperText?: string,
  multiline?: boolean,
  readOnly = false,
): DetailField {
  return {
    helperText,
    label,
    multiline,
    readOnly,
    required: REQUIRED_DETAIL_FIELD_LABELS.has(label),
    value: formatValue(value, readOnly),
  };
}

export function readOnlyField(label: string, value: unknown, helperText?: string, multiline?: boolean): DetailField {
  return field(label, value, helperText, multiline, true);
}

export function withLayout(detailField: DetailField, layout: DetailFieldLayout): DetailField {
  return {
    ...detailField,
    layout: {
      ...detailField.layout,
      ...layout,
    },
  };
}

export function typedField(
  label: string,
  value: unknown,
  type: DetailField['type'],
  helperText?: string,
  layout?: DetailFieldLayout,
): DetailField {
  return withLayout({ ...field(label, value, helperText), type }, layout ?? {});
}

export function selectField(
  label: string,
  value: unknown,
  options: Array<string>,
  helperText?: string,
  layout?: DetailFieldLayout,
): DetailField {
  return withLayout(
    {
      ...field(label, value, helperText),
      options: options.map((option) => ({ label: option, value: option })),
    },
    layout ?? {},
  );
}

export function checkboxField(
  label: string,
  value: unknown,
  helperText?: string,
  layout?: DetailFieldLayout,
): DetailField {
  return {
    ...field(label, value, helperText),
    control: 'checkbox',
    layout,
  };
}

export function checklistField(
  label: string,
  value: unknown,
  options: Array<string>,
  helperText?: string,
  layout?: DetailFieldLayout,
): DetailField {
  return {
    ...field(label, value, helperText),
    control: 'checklist',
    layout,
    options: options.map((option) => ({ label: option, value: option })),
  };
}
